/**
 * Shorthand for writing the dictionary's content: one line per field, and
 * the five fields every main record has (docs/DATA-MODEL.md → "Main record
 * types"), described once.
 */
import type { CodeDef, DataEntity, FieldDef, FieldKind, SensitivityTag } from '../types.ts'

/** [key, label, kind, tag, description, unit?] */
export type FieldRow = [string, string, FieldKind, SensitivityTag, string, string?]

export function fieldsOf(entity: DataEntity, rows: readonly FieldRow[]): FieldDef[] {
  return rows.map(([key, label, kind, tag, description, unit]) => ({
    entity,
    key,
    label,
    description,
    kind,
    tag,
    ...(unit ? { unit } : {}),
  }))
}

/** Record number, Created, Last updated, Created by, Deleted — for a main record called `noun`. */
export function standardFields(entity: DataEntity, noun: string): FieldDef[] {
  return fieldsOf(entity, [
    [
      'id',
      'Record number',
      'id',
      'general',
      `The ${noun}’s 6-digit record number, shared by no other record of any kind. Shown on screens and in addresses; numbers from 200001 up are real, lower ones are the sample.`,
    ],
    [
      'createdAt',
      'Created',
      'datetime',
      'general',
      `When the ${noun} was first saved — here or by an import. Set once by the database and never changed.`,
    ],
    [
      'updatedAt',
      'Last updated',
      'datetime',
      'general',
      `When a real value of the ${noun} last changed. Kept by the database: saving it unchanged doesn’t move it.`,
    ],
    [
      'createdBy',
      'Created by',
      'person',
      'personal',
      `The person who created it in the app. Empty when an import or an automation made it, or their account was deleted.`,
    ],
    [
      'deletedAt',
      'Deleted',
      'datetime',
      'general',
      'When it was moved to Recently deleted, or empty while it is live. Restoring empties it again; only Delete forever removes it.',
    ],
  ])
}

/** Every code of one coded field, from a map keyed by the core's own list — so none is missed. */
export function codesOf<V extends string>(
  entity: DataEntity,
  field: string,
  codes: Record<V, readonly [label: string, meaning: string]>,
  source: string,
): CodeDef[] {
  return (Object.entries(codes) as [V, readonly [string, string]][]).map(
    ([value, [label, meaning]]) => ({
      entity,
      field,
      value,
      label,
      meaning,
      confidence: 'confirmed',
      evidence: `Declared in the app’s own code (${source}).`,
    }),
  )
}
